<template>
  <div class="kits">
    <div class="header">
      <h1>Kits</h1>
      <div class="controls">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search kits..." 
          class="search-input"
        />
        <label class="checkbox-container">
          <input 
            type="checkbox" 
            v-model="includeKanban"
            class="kanban-checkbox"
          />
          <span class="checkmark">Include KanBan</span>
        </label>
        <select 
          v-if="isAdminOrManager"
          v-model="selectedCustomer"
          class="customer-select"
        >
          <option value="">All Customers</option>
          <option v-for="customer in customers" :key="customer" :value="customer">
            {{ customer }}
          </option>
        </select>
      </div>
    </div>
    
    <div v-if="isLoading" class="loading">Loading kits...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else>
      <!-- Table Container -->
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Created By</th>
              <th>Last Modified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="kit in kits" :key="kit.KitID">
              <td>{{ kit.Description }}</td>
              <td>{{ kit.UserName }}</td>
              <td>{{ formatDate(kit.LastModDate) }}</td>
              <td>
                <div class="action-buttons">
                  <button 
                    class="view-btn"
                    @click="viewLineItems(kit)"
                  >
                    View Line Items
                  </button>
                  <button 
                    v-if="kit.Code && isAdmin"
                    class="order-btn"
                    @click="openOrderKitModal(kit)"
                  >
                    Order Kit
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div class="pagination-container" v-if="pagination.totalPages > 1">
        <div class="pagination-info">
          <span>Showing {{ Math.max(1, (pagination.itemsPerPage || 10) * ((pagination.currentPage || 1) - 1) + 1) }} to {{ Math.min((pagination.itemsPerPage || 10) * (pagination.currentPage || 1), pagination.totalItems || 0) }} of {{ pagination.totalItems || 0 }} kits</span>
        </div>
        <div class="pagination-controls">
          <button 
            class="pagination-btn"
            :disabled="!pagination.hasPreviousPage"
            @click="goToPage(1)"
          >
            First
          </button>
          <button 
            class="pagination-btn"
            :disabled="!pagination.hasPreviousPage"
            @click="goToPage(pagination.currentPage - 1)"
          >
            Previous
          </button>
          
          <div class="page-numbers">
            <button 
              v-for="page in visiblePages" 
              :key="page"
              class="pagination-btn page-number"
              :class="{ active: page === pagination.currentPage }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </div>
          
          <button 
            class="pagination-btn"
            :disabled="!pagination.hasNextPage"
            @click="goToPage(pagination.currentPage + 1)"
          >
            Next
          </button>
          <button 
            class="pagination-btn"
            :disabled="!pagination.hasNextPage"
            @click="goToPage(pagination.totalPages)"
          >
            Last
          </button>
        </div>
        <div class="items-per-page">
          <label for="itemsPerPage">Items per page:</label>
          <select 
            id="itemsPerPage"
            v-model="itemsPerPage" 
            @change="changeItemsPerPage"
            class="items-select"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Order Kit Modal -->
    <div v-if="orderModal.show" class="modal-overlay">
      <div class="modal-content kit-order-modal">
        <div class="modal-header">
          <h3>Kit Order</h3>
          <button class="close-button" @click="closeOrderKitModal" :disabled="orderModal.isCreating">&times;</button>
        </div>
        <div class="modal-body">
          <template v-if="!orderModal.successId">
          <div class="form-group">
            <label for="kitOrderNumber">Order Number</label>
            <input
              id="kitOrderNumber"
              type="text"
              v-model="orderModal.orderNumber"
              class="form-input"
              placeholder="Enter order number"
              :disabled="orderModal.isCreating"
            />
          </div>

          <!-- Draft kit being added -->
          <div v-if="orderModal.kit" class="draft-kit">
            <p class="kit-desc">{{ orderModal.kit.Description }}</p>
            <p v-if="orderModal.kit.Code" class="kit-code">Part Number: {{ orderModal.kit.Code }}</p>
            <p v-else class="kit-code-missing">This kit has no Code — set KitList.Code before ordering.</p>

            <div class="pricing-bar">
              <div class="pricing-field">
                <label for="kitQty">Qty</label>
                <input
                  id="kitQty"
                  type="number"
                  min="1"
                  v-model.number="orderModal.kitQty"
                  class="form-input qty-input"
                  :disabled="orderModal.isCreating"
                />
              </div>
              <div class="pricing-field">
                <span class="pricing-label">Unit price</span>
                <span class="pricing-value">
                  <template v-if="orderModal.priceLoading">…</template>
                  <template v-else-if="orderModal.unitPrice !== null">€{{ formatPrice(orderModal.unitPrice) }}</template>
                  <template v-else>—</template>
                </span>
              </div>
              <div class="pricing-field pricing-total">
                <span class="pricing-label">Total</span>
                <span class="pricing-value">
                  <template v-if="orderModal.unitPrice !== null">€{{ formatPrice(kitOrderTotal) }}</template>
                  <template v-else>—</template>
                </span>
              </div>
            </div>
            <button
              type="button"
              class="add-cart-btn"
              @click="addDraftKitToCart"
              :disabled="orderModal.isCreating || !canAddDraftToCart"
            >
              Add to order
            </button>
          </div>

          <div v-if="orderModal.cart.length" class="kit-cart">
            <h4 class="cart-heading">Kits on this order</h4>
            <table class="cart-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Code</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Line</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in orderModal.cart" :key="line.kitId">
                  <td>{{ line.description }}</td>
                  <td>{{ line.code }}</td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      class="form-input cart-qty"
                      v-model.number="line.qty"
                      :disabled="orderModal.isCreating"
                    />
                  </td>
                  <td>
                    <template v-if="line.unitPrice !== null">€{{ formatPrice(line.unitPrice) }}</template>
                    <template v-else>—</template>
                  </td>
                  <td>
                    <template v-if="line.unitPrice !== null">€{{ formatPrice(line.qty * line.unitPrice) }}</template>
                    <template v-else>—</template>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="cart-remove-btn"
                      @click="removeCartLine(line.kitId)"
                      :disabled="orderModal.isCreating"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p class="cart-grand-total">
              Order total:
              <strong v-if="cartGrandTotal !== null">€{{ formatPrice(cartGrandTotal) }}</strong>
              <strong v-else>—</strong>
            </p>
          </div>
          <p v-else class="cart-empty-hint">
            Add one or more kits, then create the order.
          </p>

          <button
            v-if="orderModal.cart.length"
            type="button"
            class="add-another-btn"
            @click="continueSearchingKits"
            :disabled="orderModal.isCreating"
          >
            Add another kit — keep searching
          </button>
          </template>

          <div v-if="orderModal.error" class="modal-error">{{ orderModal.error }}</div>
          <div v-if="orderModal.status && !orderModal.successId" class="modal-status">{{ orderModal.status }}</div>

          <div v-if="orderModal.successId" class="modal-success modal-success-panel">
            <p class="success-title">Order created successfully</p>
            <p v-if="orderModal.soNumber" class="success-so">
              Sales order: <strong>{{ orderModal.soNumber }}</strong>
            </p>
            <p v-else class="success-so">Web orders saved. Sales order number was not returned.</p>
            <p v-if="orderModal.successIds.length > 1" class="success-detail">
              {{ orderModal.successIds.length }} kit web orders created.
            </p>
          </div>
        </div>
        <div class="modal-actions">
          <template v-if="orderModal.successId">
            <button class="cancel-button" @click="exitAfterKitOrderSuccess">Close</button>
            <button class="view-order-btn order-submit-btn" @click="goToWebOrders">View Orders</button>
          </template>
          <template v-else>
            <button class="cancel-button" @click="closeOrderKitModal" :disabled="orderModal.isCreating">Close</button>
            <button
              class="order-submit-btn"
              @click="submitKitOrder"
              :disabled="orderModal.isCreating || !canSubmitKitCart"
            >
              {{ orderModal.isCreating ? 'Creating...' : 'Create Order' }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- Sticky resume bar when cart has kits but modal is closed for searching -->
    <div
      v-if="isAdmin && !orderModal.show && !orderModal.successId && orderModal.cart.length"
      class="kit-cart-resume"
    >
      <span>{{ orderModal.cart.length }} kit{{ orderModal.cart.length === 1 ? '' : 's' }} on order</span>
      <button type="button" class="resume-order-btn" @click="resumeKitOrderModal">
        Review / create order
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'KitsView',
  data() {
    return {
      kits: [],
      searchQuery: '',
      selectedCustomer: '',
      includeKanban: false,
      customers: [],
      isLoading: true,
      error: null,
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false
      },
      itemsPerPage: 10,
      searchTimeout: null,
      userRole: '',
      orderModal: {
        show: false,
        kit: null,
        kitQty: 1,
        orderNumber: '',
        isCreating: false,
        error: null,
        status: '',
        successId: null,
        successIds: [],
        soNumber: null,
        unitPrice: null,
        priceLoading: false,
        cart: []
      }
    };
  },
  watch: {
    searchQuery() {
      this.debounceSearch();
    },
    selectedCustomer() {
      this.debounceSearch();
    },
    includeKanban() {
      this.debounceSearch();
    }
  },
  computed: {
    visiblePages() {
      const current = this.pagination.currentPage;
      const total = this.pagination.totalPages;
      const delta = 2; // Number of pages to show on each side of current page
      
      let start = Math.max(1, current - delta);
      let end = Math.min(total, current + delta);
      
      // Adjust if we're near the beginning or end
      if (current <= delta) {
        end = Math.min(total, 2 * delta + 1);
      }
      if (current >= total - delta) {
        start = Math.max(1, total - 2 * delta);
      }
      
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    isAdminOrManager() {
      return this.userRole === 'Admin' || this.userRole === 'Manager';
    },
    isAdmin() {
      return this.userRole === 'Admin';
    },
    kitOrderTotal() {
      const qty = Number(this.orderModal.kitQty) || 0;
      const unit = Number(this.orderModal.unitPrice);
      if (!qty || qty < 1 || isNaN(unit) || this.orderModal.unitPrice === null) return 0;
      return unit * qty;
    },
    canAddDraftToCart() {
      const kit = this.orderModal.kit;
      const qty = Number(this.orderModal.kitQty);
      return !!(
        kit?.Code
        && String(kit.Code).trim()
        && qty >= 1
        && !this.orderModal.priceLoading
        && this.orderModal.unitPrice !== null
        && Number(this.orderModal.unitPrice) > 0
      );
    },
    canSubmitKitCart() {
      if (!this.orderModal.orderNumber.trim()) return false;
      if (this.orderModal.cart.length) {
        return this.orderModal.cart.every(line => line.code && Number(line.qty) >= 1);
      }
      // Allow create with only the draft kit (auto-added on submit)
      return this.canAddDraftToCart;
    },
    cartGrandTotal() {
      if (!this.orderModal.cart.length) return null;
      let total = 0;
      for (const line of this.orderModal.cart) {
        if (line.unitPrice === null || isNaN(Number(line.unitPrice))) return null;
        total += Number(line.qty) * Number(line.unitPrice);
      }
      return total;
    }
  },
  methods: {
    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.pagination.currentPage = 1;
        this.fetchKits();
      }, 300);
    },
    async fetchCustomers() {
      if (!this.isAdminOrManager) {
        return;
      }
      try {
        const response = await axios.get('/customers');
        this.customers = response.data.data;
      } catch (error) {
        console.error('Error fetching customers:', error);
        this.error = error.response?.data?.message || 'Failed to fetch customers';
      }
    },
    async fetchKits() {
      this.isLoading = true;
      try {
        const response = await axios.get('/kits', {
          params: {
            searchTerm: this.searchQuery || undefined,
            customer: this.selectedCustomer || undefined,
            includeKanban: this.includeKanban,
            page: this.pagination.currentPage,
            limit: this.itemsPerPage
          }
        });
        
        // Handle the paginated response format
        console.log('Kits API response:', response.data);
        if (response.data.data && response.data.pagination) {
          this.kits = response.data.data;
          this.pagination = response.data.pagination;
          console.log('Using paginated response, pagination:', this.pagination);
        } else {
          // Fallback for non-paginated response
          this.kits = response.data;
          this.pagination = {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.data.length,
            itemsPerPage: response.data.length,
            hasNextPage: false,
            hasPreviousPage: false
          };
          console.log('Using fallback pagination:', this.pagination);
        }
      } catch (error) {
        console.error('Error fetching kits:', error);
        this.error = error.response?.data?.message || 'Failed to fetch kits';
      } finally {
        this.isLoading = false;
      }
    },
    viewLineItems(kit) {
      this.$router.push(`/kits/${kit.KitID}/line-items`);
    },
    openOrderKitModal(kit) {
      // Resume an in-progress cart: keep PO + cart, load this kit as the new draft
      if (!this.orderModal.isCreating && !this.orderModal.successId && this.orderModal.cart.length) {
        this.orderModal.show = true;
        this.orderModal.kit = { ...kit };
        this.orderModal.kitQty = 1;
        this.orderModal.error = null;
        this.orderModal.status = '';
        this.orderModal.unitPrice = null;
        this.orderModal.priceLoading = !!kit.Code;
        if (kit.Code) {
          this.loadKitUnitPrice(kit.Code);
        }
        return;
      }
      if (this.orderModal.show && !this.orderModal.isCreating && !this.orderModal.successId) {
        // Modal already open — switch draft to this kit so user can add it
        this.orderModal.kit = { ...kit };
        this.orderModal.kitQty = 1;
        this.orderModal.error = null;
        this.orderModal.status = '';
        this.orderModal.unitPrice = null;
        this.orderModal.priceLoading = !!kit.Code;
        if (kit.Code) {
          this.loadKitUnitPrice(kit.Code);
        }
        return;
      }
      this.orderModal = {
        show: true,
        kit: { ...kit },
        kitQty: 1,
        orderNumber: '',
        isCreating: false,
        error: null,
        status: '',
        successId: null,
        successIds: [],
        soNumber: null,
        unitPrice: null,
        priceLoading: !!kit.Code,
        cart: []
      };
      if (kit.Code) {
        this.loadKitUnitPrice(kit.Code);
      }
    },
    continueSearchingKits() {
      if (this.orderModal.isCreating) return;
      this.orderModal.kit = null;
      this.orderModal.kitQty = 1;
      this.orderModal.unitPrice = null;
      this.orderModal.priceLoading = false;
      this.orderModal.error = null;
      this.orderModal.status = '';
      this.orderModal.show = false;
    },
    resumeKitOrderModal() {
      this.orderModal.show = true;
      this.orderModal.kit = null;
      this.orderModal.error = null;
      this.orderModal.status = '';
    },
    async loadKitUnitPrice(code) {
      this.orderModal.priceLoading = true;
      this.orderModal.unitPrice = null;
      try {
        const resp = await axios.get('/product-validation/full-code-exists', {
          params: { code }
        });
        if (!resp.data?.exists) {
          this.orderModal.unitPrice = null;
          this.orderModal.error =
            'Unfortunately this part number is not configured in our ERP platform. Please contact IFM Customer Service to have it verified.';
          return;
        }
        this.orderModal.error = null;
        const price = Number(resp.data.sellingPrice1);
        this.orderModal.unitPrice = (!isNaN(price) && price > 0) ? price : null;
      } catch (e) {
        console.warn('Failed to load kit unit price:', e.message);
        this.orderModal.unitPrice = null;
      } finally {
        this.orderModal.priceLoading = false;
      }
    },
    formatPrice(value) {
      const n = Number(value);
      if (isNaN(n)) return '-';
      return n.toFixed(2);
    },
    closeOrderKitModal() {
      if (this.orderModal.isCreating) return;
      if (this.orderModal.successId) {
        this.exitAfterKitOrderSuccess();
        return;
      }
      // Keep cart when closing mid-order so user can keep searching
      if (this.orderModal.cart.length) {
        this.continueSearchingKits();
        return;
      }
      this.orderModal.show = false;
    },
    exitAfterKitOrderSuccess() {
      this.resetKitOrderModal();
    },
    goToWebOrders() {
      this.resetKitOrderModal();
      this.$router.push('/weborders');
    },
    resetKitOrderModal() {
      this.orderModal = {
        show: false,
        kit: null,
        kitQty: 1,
        orderNumber: '',
        isCreating: false,
        error: null,
        status: '',
        successId: null,
        successIds: [],
        soNumber: null,
        unitPrice: null,
        priceLoading: false,
        cart: []
      };
    },
    newKitBatchId() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      return `kit-batch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    },
    encodeKitOrderInstructions(instructions, batch) {
      const batchMeta = JSON.stringify({
        batchId: String(batch.batchId).trim(),
        orderNumber: String(batch.orderNumber || '').trim()
      });
      let rest = (instructions || '').toString()
        .replace(/^__KIT_BATCH__=.*\n?/m, '')
        .replace(/^__KIT_ORDER__=.*\n?/m, '')
        .replace(/^__KIT_SO__=.*\n?/m, '');
      return `__KIT_BATCH__=${batchMeta}\n${rest}`.replace(/\n+$/, '');
    },
    addDraftKitToCart() {
      const kit = this.orderModal.kit;
      if (!this.canAddDraftToCart) return;
      const kitId = kit.KitID;
      const qty = Number(this.orderModal.kitQty) || 1;
      const existing = this.orderModal.cart.find(line => line.kitId === kitId);
      if (existing) {
        existing.qty = Number(existing.qty) + qty;
        if (this.orderModal.unitPrice !== null) {
          existing.unitPrice = this.orderModal.unitPrice;
        }
      } else {
        this.orderModal.cart.push({
          kitId,
          code: String(kit.Code).trim(),
          description: kit.Description || '',
          customer: kit.Customer || null,
          qty,
          unitPrice: this.orderModal.unitPrice,
          kit: { ...kit }
        });
      }
      this.orderModal.error = null;
      this.orderModal.kit = null;
      this.orderModal.kitQty = 1;
      this.orderModal.unitPrice = null;
      this.orderModal.priceLoading = false;
    },
    removeCartLine(kitId) {
      this.orderModal.cart = this.orderModal.cart.filter(line => line.kitId !== kitId);
    },
    async createOneKitWebOrder({ kit, kitQty, unitPrice, userCustomer, currentUser, batchId, poNumber }) {
      let kitHeader = kit;
      try {
        const kitResp = await axios.get('/kits', {
          params: {
            kitid: kit.KitID,
            customer: userCustomer,
            includeKanban: true,
            page: 1,
            limit: 1
          }
        });
        const kitRows = kitResp.data?.data || [];
        if (kitRows.length > 0) {
          kitHeader = { ...kit, ...kitRows[0] };
        }
      } catch (e) {
        console.warn('Could not refresh kit header, using list row:', e.message);
      }

      if (!kitHeader.Code || !String(kitHeader.Code).trim()) {
        throw new Error(`Kit "${kit.Description || kitHeader.Code}" has no Code. Set KitList.Code before ordering.`);
      }
      if (!kitHeader.KitID && kitHeader.KitID !== 0) {
        throw new Error(`Kit "${kitHeader.Description || kitHeader.Code}" is missing KitID.`);
      }

      const kitExistsResp = await axios.get('/product-validation/full-code-exists', {
        params: { code: kitHeader.Code }
      });
      if (!kitExistsResp.data?.exists) {
        throw new Error(
          `Kit "${kit.Description || kitHeader.Code}" (${kitHeader.Code}) is not configured in our ERP platform. Please contact IFM Customer Service to have it verified.`
        );
      }

      const price = Number(unitPrice);
      if (isNaN(price) || price <= 0) {
        throw new Error(
          `Kit "${kitHeader.Description || kitHeader.Code}" has no valid IQ unit price.`
        );
      }
      const kitTotalPrice = parseFloat((price * kitQty).toFixed(2));

      const formData = new FormData();
      formData.append('Description', kitHeader.Description || `KitOrder_${kitHeader.KitID}`);
      formData.append('CreateDate', new Date().toISOString());
      formData.append('UserName', currentUser);
      formData.append('Customer', userCustomer);
      formData.append(
        'Instructions',
        this.encodeKitOrderInstructions(kitHeader.Instructions || '', {
          batchId,
          orderNumber: poNumber
        })
      );
      formData.append('LblDateCode', kitHeader.LblDateCode ?? true);
      formData.append('LblDoubles', kitHeader.LblDoubles ?? '2000');
      formData.append('Label', kitHeader.Label ?? true);
      formData.append('LblJobNumber', kitHeader.LblJobNumber ?? false);
      formData.append('OrderNumber', `${poNumber} - KIT`);
      formData.append('IsKit', 'true');
      formData.append('KitID', String(kitHeader.KitID));
      formData.append('KitQty', String(kitQty));
      formData.append('KitTotalPrice', String(kitTotalPrice));

      const orderResponse = await axios.post('/weborders', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const webOrderId = orderResponse.data?.id;
      if (!webOrderId && webOrderId !== 0) {
        throw new Error(`No web order id returned for kit ${kitHeader.Code || kit.Description}`);
      }

      return webOrderId;
    },
    async submitKitOrder() {
      // Auto-add current draft if user forgot "Add to order"
      if (this.canAddDraftToCart && this.orderModal.kit) {
        this.addDraftKitToCart();
      }

      if (!this.canSubmitKitCart) {
        this.orderModal.error = 'Add at least one kit and enter an order number.';
        return;
      }

      this.orderModal.isCreating = true;
      this.orderModal.error = null;
      this.orderModal.successId = null;
      this.orderModal.successIds = [];
      this.orderModal.soNumber = null;

      try {
        this.orderModal.status = 'Checking authentication...';
        const authResponse = await axios.get('/auth/check-auth');
        const currentUser = authResponse.data.user.email;
        const firstKit = this.orderModal.cart[0].kit;
        const userCustomer = this.isAdminOrManager
          ? (this.selectedCustomer || firstKit.Customer || this.orderModal.cart[0].customer)
          : authResponse.data.user.customer;

        if (!currentUser || !userCustomer) {
          throw new Error('Could not retrieve user / customer information');
        }

        const poNumber = this.orderModal.orderNumber.trim();
        const batchId = this.newKitBatchId();
        const webOrderIds = [];

        for (let i = 0; i < this.orderModal.cart.length; i++) {
          const line = this.orderModal.cart[i];
          const kitQty = Number(line.qty);
          if (!kitQty || kitQty < 1) {
            throw new Error(`Invalid quantity for kit ${line.code || line.description}`);
          }
          if (line.unitPrice === null || isNaN(Number(line.unitPrice)) || Number(line.unitPrice) <= 0) {
            throw new Error(`Missing IQ unit price for kit ${line.code || line.description}`);
          }
          this.orderModal.status = `Creating web order ${i + 1} of ${this.orderModal.cart.length} (${line.code || line.description})...`;
          const webOrderId = await this.createOneKitWebOrder({
            kit: line.kit,
            kitQty,
            unitPrice: line.unitPrice,
            userCustomer,
            currentUser,
            batchId,
            poNumber
          });
          webOrderIds.push(webOrderId);
        }

        this.orderModal.status = `Creating IQ sales order (${webOrderIds.length} kit line${webOrderIds.length > 1 ? 's' : ''})...`;
        const salesOrderResponse = await axios.post('/weborders/kit-batch/create-sales-order', {
          webOrderIds
        });
        const soNumber =
          salesOrderResponse.data?.soNumber ||
          salesOrderResponse.data?.result?.salesOrder?.soNumber ||
          salesOrderResponse.data?.result?.soNumber ||
          null;

        if (!salesOrderResponse.data?.success && !salesOrderResponse.data?.needsManualPricing) {
          console.warn('Sales order creation issue:', salesOrderResponse.data);
          this.orderModal.status = 'Web orders saved; sales order may need review.';
        } else if (salesOrderResponse.data?.needsManualPricing) {
          this.orderModal.status = 'Web orders saved; some lines need manual pricing.';
        } else {
          this.orderModal.status = 'Order created successfully.';
        }

        this.orderModal.soNumber = soNumber;
        this.orderModal.successIds = webOrderIds;
        this.orderModal.successId = webOrderIds[0];
        this.orderModal.cart = [];
        this.orderModal.kit = null;
        this.orderModal.status = '';
      } catch (error) {
        console.error('Kit order failed:', error);
        this.orderModal.error = error.response?.data?.message || error.message || 'Failed to create kit order';
        this.orderModal.status = '';
      } finally {
        this.orderModal.isCreating = false;
      }
    },
    async goToPage(page) {
      if (page >= 1 && page <= this.pagination.totalPages && page !== this.pagination.currentPage) {
        this.pagination.currentPage = page;
        await this.fetchKits();
      }
    },
    async changeItemsPerPage() {
      this.pagination.currentPage = 1;
      await this.fetchKits();
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    async createKit(kitData) {
      try {
        await axios.post('/kits', kitData);
        await this.fetchKits();
        this.showCreateKitModal = false;
      } catch (error) {
        console.error('Error creating kit:', error);
        this.error = error.response?.data?.message || 'Failed to create kit';
      }
    },
    async updateKit(kitId, kitData) {
      try {
        await axios.put(`/kits/${kitId}`, kitData);
        await this.fetchKits();
        this.showEditKitModal = false;
      } catch (error) {
        console.error('Error updating kit:', error);
        this.error = error.response?.data?.message || 'Failed to update kit';
      }
    },
    async deleteKit(kitId) {
      if (!confirm('Are you sure you want to delete this kit?')) {
        return;
      }
      try {
        await axios.delete(`/kits/${kitId}`);
        await this.fetchKits();
      } catch (error) {
        console.error('Error deleting kit:', error);
        this.error = error.response?.data?.message || 'Failed to delete kit';
      }
    },
    async checkAuth() {
      try {
        const response = await axios.get('/auth/check-auth');
        if (response.data.authenticated) {
          this.userRole = response.data.user.role;
          console.log('User role set to:', this.userRole); // Debug log
          return true;
        }
        this.userRole = '';
        return false;
      } catch (error) {
        console.error('Error checking auth:', error);
        this.userRole = '';
        return false;
      }
    }
  },
  async mounted() {
    try {
      const isAuthenticated = await this.checkAuth();
      console.log('Auth check result:', isAuthenticated); // Debug log
      
      if (isAuthenticated) {
        await this.fetchKits();
        if (this.isAdminOrManager) {
          console.log('Fetching customers for admin/manager'); // Debug log
          await this.fetchCustomers();
        }
      } else {
        this.error = 'Not authenticated';
      }
    } catch (error) {
      console.error('Error in mounted:', error);
      this.error = 'Failed to initialize page';
    }
  }
};
</script>

<style scoped>
.kits {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.controls {
  display: flex;
  gap: 10px;
}

.search-input {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.customer-select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
}

.kanban-checkbox {
  margin: 0;
}

.checkmark {
  user-select: none;
}

.loading {
  text-align: center;
  padding: 20px;
}

.error {
  color: red;
  text-align: center;
  padding: 20px;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.data-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.view-btn {
  padding: 6px 12px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.view-btn:hover {
  background-color: #45a049;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.order-btn {
  padding: 6px 12px;
  background-color: var(--primary-color, #dc2626);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.order-btn:hover {
  opacity: 0.9;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 480px;
  padding: 1.25rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.modal-header h3 {
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
}

.modal-body .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.form-input {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.kit-desc {
  color: #4b5563;
  margin: 0 0 0.5rem 0;
}

.kit-code {
  font-size: 0.875rem;
  color: #065f46;
  margin: 0 0 1rem 0;
}

.kit-code-missing {
  font-size: 0.875rem;
  color: #991b1b;
  margin: 0 0 1rem 0;
}

.pricing-bar {
  display: grid;
  grid-template-columns: 5.5rem minmax(0, 1fr) minmax(0, 1fr);
  gap: 2rem;
  align-items: end;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem 0.85rem 1.25rem;
  margin-bottom: 1rem;
}

.pricing-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.pricing-field label,
.pricing-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6b7280;
}

.qty-input {
  width: 100%;
  height: 2.25rem;
  text-align: center;
  font-family: inherit;
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
  padding: 0.35rem 0.4rem;
}

.pricing-value {
  height: 2.25rem;
  display: flex;
  align-items: center;
  font-family: inherit;
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
}

.pricing-total {
  align-items: flex-end;
  text-align: right;
}

.pricing-total .pricing-value {
  color: var(--primary-color, #7c3aed);
  font-size: 1.15rem;
  justify-content: flex-end;
}

.kit-order-modal {
  max-width: 720px;
  width: 95%;
}

.draft-kit {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.add-cart-btn {
  margin-top: 0.5rem;
  padding: 0.45rem 0.9rem;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.add-cart-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.kit-cart {
  margin-bottom: 1rem;
}

.cart-heading {
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  color: #374151;
}

.cart-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.cart-table th,
.cart-table td {
  text-align: left;
  padding: 0.4rem 0.35rem;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
}

.cart-qty {
  width: 4.5rem;
  padding: 0.25rem;
  text-align: center;
}

.cart-remove-btn {
  background: transparent;
  border: none;
  color: #b91c1c;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0;
}

.cart-remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cart-grand-total {
  margin: 0.75rem 0 0 0;
  text-align: right;
  font-size: 0.95rem;
}

.cart-empty-hint {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
}

.add-another-btn {
  display: block;
  width: 100%;
  margin: 0 0 1rem 0;
  padding: 0.6rem 0.9rem;
  background: #f3f4f6;
  color: #111827;
  border: 1px dashed #9ca3af;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.add-another-btn:hover:not(:disabled) {
  background: #e5e7eb;
}

.add-another-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.kit-cart-resume {
  position: fixed;
  bottom: 1.25rem;
  right: 1.25rem;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #111827;
  color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  font-size: 0.875rem;
}

.resume-order-btn {
  padding: 0.4rem 0.75rem;
  background: #fff;
  color: #111827;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
}

.price-unavailable {
  color: #9ca3af;
  font-style: italic;
}

.modal-error {
  background: #fee2e2;
  color: #991b1b;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-top: 0.5rem;
}

.modal-status {
  color: #4b5563;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.modal-success {
  background: #d1fae5;
  color: #065f46;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-top: 0.5rem;
}

.modal-success-panel {
  display: block;
}

.modal-success-panel .success-title {
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.modal-success-panel .success-so {
  margin: 0 0 0.35rem;
}

.modal-success-panel .success-detail {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
}

.view-order-btn {
  padding: 0.35rem 0.75rem;
  background: #065f46;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.cancel-button {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
}

.order-submit-btn {
  padding: 0.5rem 1rem;
  background: var(--primary-color, #dc2626);
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
}

.order-submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Pagination Styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--background);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.pagination-info {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: var(--text-primary);
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  min-width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f9fafb;
}

.pagination-btn.active {
  background-color: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.page-number {
  min-width: 2.5rem;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.items-select {
  padding: 0.375rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: white;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
}

.items-select:focus {
  outline: none;
  border-color: var(--primary-color);
}

@media (max-width: 768px) {
  .kits {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .controls {
    flex-direction: column;
    gap: 0.5rem;
  }

  .search-input,
  .customer-select {
    width: 100%;
  }

  .data-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }

  .pagination-container {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
    flex-wrap: wrap;
  }

  .pagination-info,
  .items-per-page {
    text-align: center;
  }

  .page-numbers {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>